import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { RouteGuard } from '@components/RouteGuard';
import { LoadingBlock } from '@components/LoadingBlock';
import { AdminLayout } from '@layouts/AdminLayout';
import { AdminOutletSuspenseFallback } from '@components/AdminOutletSuspenseFallback';
import { PageSkeleton } from '@components/PageSkeleton';
import { OverviewSkeleton, TablePageSkeleton } from '@components/skeletons';

import { LoginPage } from '@pages/LoginPage';
import { AccessDeniedPage } from '@pages/AccessDeniedPage';
import { ForgotPasswordPage } from '@pages/ForgotPasswordPage';
import { ResetPasswordPage } from '@pages/ResetPasswordPage';

/** Lazy admin pages — each gets its own chunk. */
const OverviewPage = lazy(() => import('@pages/OverviewPage').then((m) => ({ default: m.OverviewPage })));
const BrokersPage = lazy(() => import('@pages/BrokersPage').then((m) => ({ default: m.BrokersPage })));
const DealsPage = lazy(() => import('@pages/DealsPage').then((m) => ({ default: m.DealsPage })));
const AdminCreateDealWizardPage = lazy(() =>
  import('@pages/AdminCreateDealWizardPage').then((m) => ({ default: m.AdminCreateDealWizardPage })),
);
const SettingsPage = lazy(() => import('@pages/SettingsPage').then((m) => ({ default: m.SettingsPage })));
const AdminCreateBrokerPage = lazy(() =>
  import('@pages/AdminCreateBrokerPage').then((m) => ({ default: m.AdminCreateBrokerPage })),
);
const AdminCreateAdminPage = lazy(() =>
  import('@pages/AdminCreateAdminPage').then((m) => ({ default: m.AdminCreateAdminPage })),
);
const BrokerDetailPage = lazy(() => import('@pages/BrokerDetailPage').then((m) => ({ default: m.BrokerDetailPage })));
const CommissionReportsPage = lazy(() =>
  import('@pages/CommissionReportsPage').then((m) => ({ default: m.CommissionReportsPage })),
);
const DealDetailPage = lazy(() => import('@pages/DealDetailPage').then((m) => ({ default: m.DealDetailPage })));
const SystemLogsPage = lazy(() => import('@pages/SystemLogsPage').then((m) => ({ default: m.SystemLogsPage })));
const WithdrawalsPage = lazy(() =>
  import('@pages/WithdrawalsPage').then((m) => ({ default: m.WithdrawalsPage })),
);
const TransactionsPage = lazy(() =>
  import('@pages/TransactionsPage').then((m) => ({ default: m.TransactionsPage })),
);

const LoadingFallback = () => <LoadingBlock className="min-h-screen" />;

function RootLayout() {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <Outlet />
    </Suspense>
  );
}

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/forgot-password', element: <ForgotPasswordPage /> },
      { path: '/reset-password', element: <ResetPasswordPage /> },
      {
        path: '/access-denied',
        element: (
          <RouteGuard>
            <AccessDeniedPage />
          </RouteGuard>
        ),
      },
      {
        path: '/',
        element: (
          <RouteGuard>
            <AdminLayout />
          </RouteGuard>
        ),
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<AdminOutletSuspenseFallback><OverviewSkeleton /></AdminOutletSuspenseFallback>}>
                <OverviewPage />
              </Suspense>
            ),
          },
          {
            path: 'brokers',
            element: (
              <Suspense
                fallback={
                  <AdminOutletSuspenseFallback>
                    <TablePageSkeleton showPageHeading showPrimaryAction showSearch tableColumns={7} />
                  </AdminOutletSuspenseFallback>
                }
              >
                <BrokersPage />
              </Suspense>
            ),
          },
          {
            path: 'brokers/new',
            element: (
              <Suspense fallback={<AdminOutletSuspenseFallback><PageSkeleton /></AdminOutletSuspenseFallback>}>
                <AdminCreateBrokerPage />
              </Suspense>
            ),
          },
          {
            path: 'brokers/:id',
            element: (
              <Suspense fallback={<AdminOutletSuspenseFallback><PageSkeleton /></AdminOutletSuspenseFallback>}>
                <BrokerDetailPage />
              </Suspense>
            ),
          },
          {
            path: 'brokerage',
            element: (
              <Suspense
                fallback={
                  <AdminOutletSuspenseFallback>
                    <TablePageSkeleton showPageHeading showPrimaryAction showSearch={false} tableColumns={7} />
                  </AdminOutletSuspenseFallback>
                }
              >
                <DealsPage />
              </Suspense>
            ),
          },
          { path: 'deals', element: <Navigate to="/brokerage" replace /> },
          {
            path: 'brokerage/new',
            element: (
              <Suspense fallback={<AdminOutletSuspenseFallback><PageSkeleton /></AdminOutletSuspenseFallback>}>
                <AdminCreateDealWizardPage />
              </Suspense>
            ),
          },
          { path: 'deals/new', element: <Navigate to="/brokerage/new" replace /> },
          {
            path: 'brokerage/:id',
            element: (
              <Suspense fallback={<AdminOutletSuspenseFallback><PageSkeleton /></AdminOutletSuspenseFallback>}>
                <DealDetailPage />
              </Suspense>
            ),
          },
          {
            path: 'deals/:id',
            element: (
              <Suspense fallback={<AdminOutletSuspenseFallback><PageSkeleton /></AdminOutletSuspenseFallback>}>
                <DealDetailPage />
              </Suspense>
            ),
          },
          {
            path: 'commission-reports',
            element: (
              <Suspense
                fallback={
                  <AdminOutletSuspenseFallback>
                    <TablePageSkeleton showPageHeading showSecondaryAction filterSelectCount={3} tableColumns={5} />
                  </AdminOutletSuspenseFallback>
                }
              >
                <CommissionReportsPage />
              </Suspense>
            ),
          },
          {
            path: 'withdrawals',
            element: (
              <Suspense
                fallback={
                  <AdminOutletSuspenseFallback>
                    <TablePageSkeleton showPageHeading showSearch tableColumns={9} />
                  </AdminOutletSuspenseFallback>
                }
              >
                <WithdrawalsPage />
              </Suspense>
            ),
          },
          {
            path: 'transactions',
            element: (
              <Suspense
                fallback={
                  <AdminOutletSuspenseFallback>
                    <TablePageSkeleton showPageHeading showSecondaryAction tableColumns={9} />
                  </AdminOutletSuspenseFallback>
                }
              >
                <TransactionsPage />
              </Suspense>
            ),
          },
          {
            path: 'system-logs',
            element: (
              <Suspense fallback={<AdminOutletSuspenseFallback><TablePageSkeleton showPageHeading tableColumns={4} /></AdminOutletSuspenseFallback>}>
                <SystemLogsPage />
              </Suspense>
            ),
          },
          {
            path: 'settings',
            element: (
              <Suspense fallback={<AdminOutletSuspenseFallback><PageSkeleton /></AdminOutletSuspenseFallback>}>
                <SettingsPage />
              </Suspense>
            ),
          },
          {
            path: 'settings/accounts/new',
            element: (
              <Suspense fallback={<AdminOutletSuspenseFallback><PageSkeleton /></AdminOutletSuspenseFallback>}>
                <AdminCreateAdminPage />
              </Suspense>
            ),
          },
          {
            path: 'settings/roles-permissions',
            element: <Navigate to="/settings?tab=roles-permissions" replace />,
          },
          { path: '*', element: <Navigate to="/" replace /> },
        ],
      },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
]);
